import { Button } from "@/components/ui/button";
import { CalendarDays, Search, Handshake, BarChart } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import CoraaAIDialog from "@/components/givf/CoraaAIDialog";

// Animation style to match other sections
const fadeIn = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" },
};

export function VolunteeringHubSection() {
  const navigate = useNavigate();
  const [isCoraaDialogOpen, setIsCoraaDialogOpen] = useState(false);
  const features = [
    {
      icon: CalendarDays,
      title: "Observance-Aligned Campaigns",
      description:
        "Launch campaigns mapped to UN days, SDGs, and trending causes.",
    },
    {
      icon: Search,
      title: "Peer Insights & Benchmarking",
      description:
        "See how leading companies engage volunteers, by theme, format, and region.",
    },
    {
      icon: Handshake,
      title: "Discover Vetted Nonprofits",
      description:
        "Find the right partners by impact area, location, and compliance.",
    },
    {
      icon: BarChart,
      title: "Smarter Program Design",
      description:
        "Build programs that boost engagement and deliver measurable results.",
    },
  ];

  return (
    <>
      <section className="py-20 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Content */}
            <motion.div className="order-2 lg:order-1" {...fadeIn}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                CORAA for Volunteering Teams
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Design smarter volunteering programs backed by peer insights,
                observance calendars, and vetted nonprofits.
              </p>

              <div className="space-y-6 mb-10">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="flex gap-4 items-start"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.7,
                      delay: index * 0.12,
                      ease: "easeOut",
                    }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#2E7D32]/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-[#2E7D32]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Button
                className="bg-[#17863d] hover:bg-[#146c31] text-white font-bold text-lg px-8 py-4 rounded-lg transition-all duration-200 w-full max-w-xl mx-auto"
                onClick={() => setIsCoraaDialogOpen(true)}
              >
                Explore Volunteering with CORAA, See Campaign Ideas
              </Button>
            </motion.div>

            {/* Right side - Visual Representation */}
            <motion.div
              className="order-1 lg:order-2 flex justify-center items-center h-full"
              {...fadeIn}
            >
              <div className="flex justify-center items-center">
                <img
                  src="/images/CORAA-Volunteering.png"
                  alt="CORAA Volunteering"
                  className="w-full max-w-[350px] h-auto object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <CoraaAIDialog
        open={isCoraaDialogOpen}
        initialInput="Tell me some campaign ideas"
        onClose={() => setIsCoraaDialogOpen(false)}
      />
    </>
  );
}
